/**
 * VoiceConversationEngine - Reusable voice conversation UI component
 * 
 * Generic voice conversation component extracted from VoiceConversation.jsx.
 * Handles all voice plumbing (STT, TTS, echo filtering, pause detection, 
 * input gating) through the useVoiceConversationEngine hook.
 * 
 * The "brain" is pluggable via the onPauseDetected prop -- each consumer 
 * injects its own question-generation logic.
 * 
 * Used by:
 * - VoiceConversation.jsx (Symptom Checker) 
 * - CheckInConversation.jsx (SecureLink patient intake)
 * - Future features (MedReview, Discharge, etc.)
 */

import { tokens } from '@healthdesk/shared-utils';
import React, { useCallback, useEffect, useId } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  LinearProgress,
  Fade,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { contentCardSx } from '../../styles/standaloneLayoutTokens';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import PersonIcon from '@mui/icons-material/Person';

import useVoiceConversationEngine from './useVoiceConversationEngine';
import useAudioLevel from './useAudioLevel';
import VoiceLevelIndicator from './VoiceLevelIndicator';

/**
 * @param {Object} props
 * @param {function} props.onPauseDetected - Called when user finishes speaking.
 *   Receives ({ userTranscript, conversationLog, questionsAsked, context }).
 *   Must return { question: string|null, isComplete: boolean }.
 * @param {string} props.title - Display title for the conversation
 * @param {string} [props.openingMessage] - First thing the AI says
 * @param {number} [props.maxQuestions] - For progress bar display (0 = no progress)
 * @param {number} [props.pauseDuration] - Pause duration in ms
 * @param {string} [props.language='en-US'] - Language code
 * @param {function} [props.onLanguageChange] - Called when language changes
 * @param {string} [props.ttsProvider='openai'] - TTS provider
 * @param {boolean} [props.compact=false] - Compact mode
 * @param {boolean} [props.showLanguageSelector=true] - Show language dropdown
 * @param {function} props.onComplete - Called with conversationLog when ended
 * @param {function} [props.onSkip] - If provided, shows a skip button
 * @param {Object} [props.context] - Opaque context passed to onPauseDetected
 * @param {function} [props.onUserSpeech] - Called with each user utterance
 * @param {function} [props.onLogEntry] - Called with (newEntry, fullLog) on each log change
 * @param {function} [props.onLiveTranscript] - Called with (text) for live/in-progress STT; '' when committed
 * @param {boolean} [props.autoStart=false] - Auto-start on mount
 * @param {boolean} [props.askFirstQuestionAfterOpening=false] - If true, after the opening message
 *   the engine immediately asks the first question (no wait for user). Used for check-in.
 * @param {React.ReactNode} [props.headerExtra] - Extra content in header area
 * @param {React.ReactNode} [props.beforeStart] - Content shown before start (e.g. specialty selector)
 * 
 * Controlled state (optional):
 * @param {Array} [props.conversationLog] - Controlled conversation log
 * @param {function} [props.onConversationLogChange] - Setter for controlled log
 * @param {Array} [props.questionsAsked] - Controlled questions asked
 * @param {function} [props.onQuestionsAskedChange] - Setter for controlled questions
 */
function VoiceConversationEngine({
  onPauseDetected,
  title = 'Voice Conversation',
  openingMessage = 'Hello! How can I help you today?',
  maxQuestions = 0,
  pauseDuration,
  language = 'en-US',
  onLanguageChange,
  ttsProvider = 'openai',
  ttsVoice,
  compact = false,
  showLanguageSelector = true,
  onComplete,
  onStartRequested,
  onSkip,
  context,
  onUserSpeech,
  onLogEntry,
  onLiveTranscript,
  autoStart = false,
  askFirstQuestionAfterOpening = false,
  askDisclaimerAfterOpening = true,
  headerExtra,
  beforeStart,
  // Controlled state
  conversationLog: controlledConversationLog,
  onConversationLogChange,
  questionsAsked: controlledQuestionsAsked,
  onQuestionsAskedChange,
}) {
  const languageLabelId = useId();
  const engine = useVoiceConversationEngine({
    onPauseDetected,
    openingMessage,
    pauseDuration,
    language,
    onLanguageChange,
    ttsProvider,
    ttsVoice,
    onComplete,
    onUserSpeech,
    onLogEntry,
    onLiveTranscript,
    context,
    askFirstQuestionAfterOpening,
    askDisclaimerAfterOpening,
    controlledConversationLog,
    onConversationLogChange,
    controlledQuestionsAsked,
    onQuestionsAskedChange,
  });

  const {
    isActive,
    isStarting,
    isListening,
    isSpeaking,
    currentQuestion,
    questionsAsked,
    conversationLog,
    audioConfirmed,
    isAcceptingInput,
    speechSupported,
    endConversationAcknowledged,
    startConversation,
    stopConversation,
    conversationScrollRef,
    supportedLanguages,
  } = engine;

  // Mic level monitoring — active when listening and accepting input
  const audioLevel = useAudioLevel(
    isActive && isListening && audioConfirmed && isAcceptingInput && !isSpeaking
  );

  // Auto-start
  useEffect(() => {
    if (autoStart && !isActive) {
      const canStart = typeof onStartRequested === 'function' ? onStartRequested() : true;
      if (canStart !== false) startConversation();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStartClick = useCallback(() => {
    const canStart = typeof onStartRequested === 'function' ? onStartRequested() : true;
    if (canStart !== false) startConversation();
  }, [onStartRequested, startConversation]);

  // Auto-scroll conversation display
  useEffect(() => {
    if (conversationScrollRef.current) {
      requestAnimationFrame(() => {
        if (conversationScrollRef.current) {
          conversationScrollRef.current.scrollTop = conversationScrollRef.current.scrollHeight;
        }
      });
    }
  }, [conversationLog, conversationScrollRef]);

  if (!speechSupported) {
    return (
      <Box sx={{ p: compact ? 2 : 3, textAlign: 'center', bgcolor: 'warning.lighter', borderRadius: 2 }}>
        <Typography color="warning.dark" variant={compact ? 'body2' : 'body1'}>
          Voice conversation is not supported in your browser.
          Please use Chrome, Edge, or Safari for voice features.
        </Typography>
      </Box>
    );
  }

  const progress = maxQuestions > 0 ? Math.min(100, (questionsAsked.length / maxQuestions) * 100) : 0;

  const content = (
    <>
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={compact ? 1 : 2}>
        <Typography variant={compact ? 'subtitle1' : 'h6'} fontWeight={600}>
          {title}
        </Typography>

        {isActive && (
          <Box display="flex" alignItems="center" gap={1}>
            {isStarting && !isSpeaking && (
              <Chip
                icon={<VolumeUpIcon />}
                label="Starting..."
                color="info"
                size="small"
                sx={{ animation: 'pulse 0.8s infinite' }}
              />
            )}
            {isListening && audioConfirmed && isAcceptingInput && !isStarting && (
              <Chip
                icon={<MicIcon />}
                label={compact ? 'Listening' : 'Listening...'}
                color="success"
                size="small"
                sx={{ animation: 'pulse 1.5s infinite' }}
              />
            )}
            {isListening && (!audioConfirmed || !isAcceptingInput) && !isSpeaking && !isStarting && (
              <Chip
                icon={<MicIcon />}
                label="Initializing..."
                color="warning"
                size="small"
                sx={{ animation: 'pulse 1s infinite' }}
              />
            )}
            {isSpeaking && (
              <Chip
                icon={<VolumeUpIcon />}
                label={compact ? 'Speaking' : 'Speaking...'}
                color="primary"
                size="small"
              />
            )}
          </Box>
        )}
      </Box>

      {headerExtra}

      {/* Progress / status bar */}
      {isActive && (
        <Box sx={{ mb: compact ? 1 : 2 }}>
          <LinearProgress
            variant={
              isStarting || isSpeaking || (isListening && (!audioConfirmed || !isAcceptingInput))
                ? 'indeterminate'
                : 'determinate'
            }
            value={
              maxQuestions > 0 && isListening && audioConfirmed && isAcceptingInput && !isStarting
                ? progress
                : isListening && audioConfirmed && isAcceptingInput && !isStarting
                  ? 100
                  : 0
            }
            color={
              isStarting ? 'info' : isSpeaking ? 'primary' : isListening && audioConfirmed && isAcceptingInput ? 'success' : 'warning'
            }
            sx={{ height: compact ? 3 : 4, borderRadius: 2 }}
          />
          {isStarting && !isSpeaking && (
            <Typography variant="caption" color="info.main" sx={{ mt: 0.5, display: 'block', textAlign: 'center' }}>
              Preparing to speak... one moment
            </Typography>
          )}
          {isListening && (!audioConfirmed || !isAcceptingInput) && !isSpeaking && !isStarting && (
            <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, display: 'block', textAlign: 'center' }}>
              Initializing... please wait
            </Typography>
          )}
          {maxQuestions > 0 && isListening && audioConfirmed && isAcceptingInput && !isStarting && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', textAlign: 'center' }}>
              Question {Math.min(questionsAsked.length, maxQuestions)} of {maxQuestions}
            </Typography>
          )}
        </Box>
      )}

      {/* Microphone level indicator */}
      {isActive && !isStarting && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: compact ? 1 : 1.5 }}>
          <VoiceLevelIndicator
            level={audioLevel}
            muted={isSpeaking || !isAcceptingInput}
            height={compact ? 22 : 28}
            userHasSpoken={conversationLog.some((e) => e.speaker === 'user')}
          />
        </Box>
      )}

      {/* Conversation display - show log whenever we have entries (including after conversation ends)
          so the same running transcript stays visible with no change / no separate transcript. */}
      {conversationLog.length > 0 && (
        <Box
          ref={conversationScrollRef}
          sx={{
            maxHeight: compact ? 150 : 200,
            overflow: 'auto',
            mb: compact ? 1 : 2,
            p: compact ? 1 : 1.5,
            bgcolor: 'background.paper',
            borderRadius: 2,
            flex: compact ? 1 : 'none',
          }}
        >
          {conversationLog.map((entry, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                mb: 1,
                opacity: index === conversationLog.length - 1 ? 1 : 0.7,
              }}
            >
              {entry.speaker === 'user' ? (
                <PersonIcon fontSize="small" color="action" />
              ) : (
                <VolumeUpIcon fontSize="small" color="primary" />
              )}
              <Typography
                variant="body2"
                sx={{
                  fontStyle: entry.speaker === 'assistant' ? 'italic' : 'normal',
                  color: entry.speaker === 'assistant' ? 'primary.main' : 'text.primary',
                }}
              >
                {entry.text}
              </Typography>
            </Box>
          ))}
          {!isActive && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
              Conversation has ended.
            </Typography>
          )}
        </Box>
      )}

      {/* Current question */}
      <Fade in={!!currentQuestion}>
        <Box sx={{ mb: compact ? 1 : 2, p: compact ? 1.5 : 2, bgcolor: 'primary.lighter', borderRadius: 2 }}>
          <Typography variant={compact ? 'body2' : 'body1'} color="primary.dark" fontWeight={500}>
            "{currentQuestion}"
          </Typography>
        </Box>
      </Fade>

      {/* Before-start content (e.g. specialty selector) - only when conversation hasn't started yet */}
      {!isActive && conversationLog.length === 0 && beforeStart}

      {/* Language selector + Start button - only when conversation hasn't started yet */}
      {!isActive && conversationLog.length === 0 && (
        <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          {showLanguageSelector ? (
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id={languageLabelId}>Language</InputLabel>
              <Select
                labelId={languageLabelId}
                value={language}
                label="Language"
                onChange={(e) => onLanguageChange?.(e.target.value)}
              >
                {supportedLanguages.map((lang) => (
                  <MenuItem key={lang.code} value={lang.code}>
                    {lang.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Box sx={{ flex: 1, minWidth: 0 }} />
          )}
          <Button
            variant="contained"
            size={compact ? 'medium' : 'large'}
            startIcon={<MicIcon />}
            onClick={handleStartClick}
            sx={{
              px: compact ? 3 : 4,
              py: compact ? 1 : 1.5,
              borderRadius: 3,
              fontSize: compact ? '0.95rem' : '1.1rem',
            }}
          >
            {compact ? 'Start Conversation' : 'Start Voice Conversation'}
          </Button>
        </Box>
      )}

      {/* Controls */}
      <Box display="flex" justifyContent="center" gap={2} sx={{ mt: compact ? 1 : 0 }}>
        {isActive && (isAcceptingInput || endConversationAcknowledged) && !isStarting && (
          <Button
            variant="contained"
            size={compact ? 'medium' : 'large'}
            startIcon={<StopIcon />}
            onClick={stopConversation}
            aria-pressed={endConversationAcknowledged}
            sx={{
              px: compact ? 3 : 4,
              py: compact ? 1 : 1.5,
              borderRadius: 3,
              bgcolor: tokens.colors.msiRed.light,
              '&:hover': { bgcolor: '#6b1538' },
              ...(endConversationAcknowledged && {
                boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.35)',
                transform: 'translateY(1px)',
              }),
            }}
          >
            End Conversation
          </Button>
        )}
        {onSkip && !isActive && conversationLog.length === 0 && (
          <Button
            variant="outlined"
            color="inherit"
            size={compact ? 'medium' : 'large'}
            onClick={onSkip}
            sx={{ px: compact ? 3 : 4, py: compact ? 1 : 1.5, borderRadius: 3 }}
          >
            Skip
          </Button>
        )}
      </Box>

      {/* Instructions - only when conversation hasn't started yet */}
      {!isActive && conversationLog.length === 0 && !compact && (
        <Typography
          variant="body2"
          color="text.secondary"
          textAlign="center"
          sx={{ mt: 2 }}
        >
          Click to start a voice conversation. I'll listen and ask questions when you pause.
        </Typography>
      )}

      {/* Pulse animation */}
      <style>{`
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.6; }
          100% { opacity: 1; }
        }
      `}</style>
    </>
  );

  if (compact) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {content}
      </Box>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        ...contentCardSx,
        p: 3,
        background: isActive
          ? 'linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%)'
          : 'background.paper',
        transition: 'background 0.3s ease',
      }}
    >
      {content}
    </Paper>
  );
}

export default VoiceConversationEngine;
